import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Music, Plus, X, Check, ExternalLink, Send, Link2 } from 'lucide-react';
import { formatEastern } from '@/lib/format';

// Song approval: the editor sends song choices ONCE; the client keeps any of them and sends
// links to their own for anything that is not right (operator ruling, 2026-09-15). Sets sent
// before the ruling carry no `policy` and keep the old approve / ask-for-changes answer.
//
// The editor side has NO free-text field, deliberately. Editors don't converse with
// clients (internal_messages / client_messages are separate channels), and this
// feature holds that line structurally: a short heading names a moment, and
// everything else is a URL. Adding a message box here would quietly reopen the
// editor-to-client channel. See backend/routes/song_approval.py for the full rationale.

const HEADING_MAX = 60;
const NOTE_MAX = 1000;     // the server's CLIENT_NOTE_MAX (backend/routes/song_approval.py; a backend test holds the two equal)

// The client's note. Ryan, 2026-10-12, after a client's note stopped mid-word at the limit with nothing said, in a box
// two lines tall: "1000 char limit ... and make sure they can read what they wrote easily". The box grows with what is
// written (up to 20 lines, then it scrolls, and it can be dragged taller), the text is the size of the rest of the
// panel's reading text, and a count under it says how much room is left before the limit is reached, not at it.
function NoteBox({ value, onChange, placeholder }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 420)}px`;
  }, [value]);
  const used = value.length;
  const full = used >= NOTE_MAX;
  const n = (x) => x.toLocaleString('en-US');
  return (
    <div data-testid="song-client-note-box">
      <textarea ref={ref} value={value} onChange={e => onChange(e.target.value)} rows={3} maxLength={NOTE_MAX}
        data-testid="song-client-note" placeholder={placeholder}
        className="w-full bg-ug-surface border border-ug-border-strong rounded px-2.5 py-2 text-[13px] leading-relaxed text-zinc-100 placeholder:text-slate-600 resize-y" />
      {used > 0 && (
        <p data-testid="song-client-note-count" data-tone={full ? 'warning' : undefined}
          className={`tap-auto mt-0.5 ${full ? 'text-[11px] leading-relaxed text-ug-status-red font-semibold' : 'text-[10px] text-right text-slate-500'}`}>
          {full ? `That is the limit (${n(NOTE_MAX)} characters). Shorten it, or send the rest in your project chat.`
                : `${n(used)} of ${n(NOTE_MAX)} characters`}
        </p>
      )}
    </div>
  );
}
const MAX_SECTIONS = 10;
const MAX_LINKS = 5;

// The operator's approved wording, shared with the client's email (backend
// email_service.SONG_ONCE_POLICY). The email says "from when this email was sent"; the
// panel is not an email, so it says "from when we sent them". Nothing else differs.
export const SONG_ONCE_POLICY = "To keep every client's film on schedule, we send song choices only once. Choose the song(s) you'd like us to use. If you don't like one or all of our options, you must send links to your own song(s) instead. You have 24 hours from when we sent them to reply. If we don't hear from you by then, your film loses its spot in the editing queue and other films move ahead. Any song questions after that go through your project chat.";

// The same policy for a project that is not scheduled yet (operator ruling 2026-09-16),
// mirroring backend email_service.SONG_ONCE_POLICY_UNTIMED word for word. It keeps the one-set
// rule and the "send your own if ours are wrong" instruction, and drops every word about 24
// hours and losing a place in the queue, because neither is true for this project. It still
// gives a reason to answer: the edit cannot start without songs.
export const SONG_ONCE_POLICY_UNTIMED = "To keep every client's film on schedule, we send song choices only once. Choose the song(s) you'd like us to use. If you don't like one or all of our options, please send links to your own song(s) instead. Your film does not have a start date yet, so there is no time limit on this one. We cannot start editing until we have your songs, so the sooner you send them, the sooner your film gets under way. Any song questions after that go through your project chat.";

// The same policy once the deadline has passed. The late notice beneath it then states what is
// ACTUALLY true for this project (song_wait.consequence), so this keeps only the one-set rule and how
// to answer: the two timing sentences ("You have 24 hours... your film loses its spot...") are dropped
// rather than left to contradict it. Otherwise it is the approved wording, word for word. Without this,
// a shadow-mode client read "loses its spot" above the fallback, and a live client with nothing behind
// them read "loses its spot" above "every day moves your delivery date back" (operator ruling 2026-09-16).
export const SONG_ONCE_POLICY_LATE = "To keep every client's film on schedule, we send song choices only once. Choose the song(s) you'd like us to use. If you don't like one or all of our options, you must send links to your own song(s) instead. Any song questions after that go through your project chat.";

// The same policy for a set sent while the song deadline rule was not live (operator ruling 2026-09-16),
// mirroring backend email_service.SONG_ONCE_POLICY_NOT_LIVE word for word: the one-set rule and how to
// answer, then "as soon as you can", with no hours and no lost spot, because nothing will enforce either.
// Not the untimed paragraph: this film usually has a start date.
export const SONG_ONCE_POLICY_NOT_LIVE = "To keep every client's film on schedule, we send song choices only once. Choose the song(s) you'd like us to use. If you don't like one or all of our options, you must send links to your own song(s) instead. Please reply as soon as you can: your editor needs your songs to keep working on your film. Any song questions after that go through your project chat.";

// THE POLICY PARAGRAPH IS CHOSEN BY THE SERVER. song_wait.terms is the set's stamp, decided when it was
// sent (backend app/song_deadline.send_stamp: a delivery date AND the rule live, read at that moment) and
// true for the set's whole life; this only puts each choice into words. Nothing here reads the mode or the
// proposal's fields to work it out. Past the deadline the late paragraph replaces the timed one, because
// the notice beneath it then says what is actually true (song_wait.consequence). With no song_wait at all
// (the film is already cut, and the sweep closes the set), no timing claim is made.
const POLICY_BY_TERMS = { timed: SONG_ONCE_POLICY, no_date: SONG_ONCE_POLICY_UNTIMED, not_live: SONG_ONCE_POLICY_NOT_LIVE };
export function policyFor(wait) {
  if (!wait) return SONG_ONCE_POLICY_LATE;
  if (wait.consequence) return SONG_ONCE_POLICY_LATE;
  return POLICY_BY_TERMS[wait.terms] || SONG_ONCE_POLICY_LATE;
}

const isOnce = (proposal) => proposal?.policy === 'once';

const blankSection = () => ({ heading: '', links: [''] });

// Said to an editor sending more than one section with a title missing, word for word the server's refusal
// (backend routes/song_approval.py TITLES_NEEDED; a backend test holds the two equal). The client sees a blank title
// as "Part 2" and cannot tell which part of the video it is for.
export const TITLES_NEEDED = 'Give each section a title (Intro, Ceremony, Dance...). With more than one section the client sees the titles, and without them cannot tell which part of the video each one is for.';

const hostOf = (url) => {
  try { return new URL(url.includes('://') ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
  catch { return url; }
};

/* WHAT THE CLIENT PICKED, LEGIBLY.
 *
 * This used to render `selected_links.map(hostOf).join(', ')`, so two tracks from
 * the same library both printed as "musicbed.com" — "Client picked: musicbed.com,
 * musicbed.com". That is worse than uninformative: a repeated identical token reads
 * as a rendering fault, so the editor doubts the data rather than the display. The
 * data was always correct; selected_links holds full URLs, validated on the way in
 * against the links actually offered.
 *
 * NO TRACK NAME IS STORED ANYWHERE and none can be — `_clean_sections` coerces every
 * link with `_normalize_link(str(l))`, so a link is always a bare string. The label
 * therefore has to come out of the URL.
 *
 * FALLBACK LADDER, ordered so nothing misleading is ever rendered:
 *   no path segments             -> no track label, show the host
 *   last segment not numeric     -> use it           (…/songs/golden-by-utah)
 *   last numeric, prev not generic -> use prev       (…/golden/24680 -> "golden")
 *   last numeric, prev generic   -> use the number   (…/song/24680 -> "24680")
 *
 * The third rung tests the PREVIOUS segment against a denylist of generic path
 * words, not against a slug shape. An earlier version required a hyphen, which
 * quietly discarded every single-word track name — "…/songs/golden/24680" rendered
 * as "24680" and threw away the one useful thing in the URL. Anything that is not a
 * known routing word is plausibly a name.
 *
 * The fourth rung matters for the opposite reason: rendering "song" as a track name
 * from a generic path word would look authoritative and be wrong, and a host
 * fallback would collide again for two tracks from the same provider — which is the
 * bug this whole thing fixes. A bare ID is honest and unique.
 *
 * The full URL is never lost — every entry is an anchor carrying href and title. */
const MAX_LABEL = 80;   // safety net only — CSS `truncate` does the visible clipping
const _clip = (t) => (t.length > MAX_LABEL ? `${t.slice(0, MAX_LABEL - 1)}…` : t);

/* Routing words, not track names. A segment matching one of these sits between the
 * host and the thing we actually want, so it must never be rendered as a title.
 * Single characters are here for the same reason (…/s/24680). */
const GENERIC_SEGMENTS = new Set(
  ['song', 'songs', 'track', 'tracks', 'audio', 'music', 'browse']);
const isGenericSegment = (seg) => {
  const t = (seg || '').trim().toLowerCase();
  return t.length <= 1 || GENERIC_SEGMENTS.has(t);
};

const trackLabel = (url) => {
  try {
    const u = new URL(url.includes('://') ? url : `https://${url}`);
    const segs = u.pathname.split('/').filter(Boolean);
    if (!segs.length) return null;
    const last = segs[segs.length - 1];
    if (!/^\d+$/.test(last)) return _clip(decodeURIComponent(last));
    const prev = segs[segs.length - 2];
    if (prev && !isGenericSegment(prev)) return _clip(decodeURIComponent(prev));
    return _clip(last);
  } catch { return null; }
};

/** Which section offered this link. Recoverable by lookup because selected_links is
 *  validated against the offered set, so entries are exact matches for a section's
 *  link — no schema change needed to know where a pick belongs. Never invented: a
 *  blank heading or an unmatched link renders no prefix at all. */
const sectionFor = (proposal, link) => {
  for (const sec of proposal.sections || []) {
    if ((sec.links || []).includes(link)) return (sec.heading || '').trim() || null;
  }
  return null;
};


// The 24-hour rule (backend app/song_deadline.py): calendar hours, weekends included.
// `deadline_at` is stamped on the proposal by the server; older proposals without it
// are treated as never having had a clock.
export function deadlineState(proposal, now = Date.now()) {
  if (!proposal?.deadline_at) return null;
  const left = (new Date(proposal.deadline_at).getTime() - now) / 3600000;
  if (proposal.status === 'expired') return { expired: true, hoursLeft: 0 };
  if (proposal.status !== 'pending') return null;
  return { expired: left <= 0, hoursLeft: Math.max(0, left) };
}
export function hoursWords(h) {
  if (h >= 2) return `${Math.floor(h)} hours`;
  if (h >= 1) return '1 hour';
  const m = Math.max(1, Math.round(h * 60)); return `${m} minute${m === 1 ? '' : 's'}`;
}
// 'Tuesday, Sep 29' from a YYYY-MM-DD date: the same words as backend
// app/time_utils.eastern_day_words, so the panel and the email name a date identically.
// Parsed as a calendar day, never as an instant, which would shift it a day west of UTC.
export function dayWords(ymd) {
  if (!ymd) return '';
  const d = new Date(`${ymd}T00:00:00`);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

/* The board chip for a set that is out and unanswered. Lives here with the rest of the song
 * surfaces, shaped like the board's other row chips (LinkStateChip / UploadStatusChip /
 * DriveAccessChip in components/LinkCheck.js) so the desktop and mobile rows share ONE copy:
 * the two rows previously held the same markup twice, and the untimed state would have had
 * to be written into both.
 *
 * `song_wait.timed` is the branch, NEVER a falsy hours_left: an untimed set reports no hours
 * at all, and `Math.round(null)` is 0, which would have rendered "Songs 0h" -- the most
 * alarming thing on the row -- for the one project that has no deadline whatsoever. */
export function SongWaitChip({ project, idSuffix = '' }) {
  const w = project.song_wait;
  if (!w) return null;
  if (!w.timed) return (
    <span data-testid={`song-wait-untimed${idSuffix}`}
      title={w.terms === 'not_live'
        ? 'Song choices sent while the song deadline rule was not switched on, so there is no time limit and nothing moves in the queue.'
        : 'Song choices sent. This project has no delivery date yet, so there is no time limit and nothing moves in the queue.'}
      className="tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10">
      <Music className="w-2.5 h-2.5 shrink-0" /> Songs sent
    </span>
  );
  const hours = Math.max(0, Math.round(w.hours_left));
  const late = w.status === 'expired' || w.status === 'overdue';
  // A board row does not carry the mode, so the chip states only what happened, never what it costs:
  // that is decided per project (song_wait.consequence) and said in the drawer.
  const title = late ? 'Song choices unanswered past 24 hours.' : `Client has ${hours}h to answer the song choices.`;
  return (
    <span data-testid={`song-wait-${w.status}${idSuffix}`} title={title}
      className={`tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${
        late || w.hours_left <= 4 ? 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10' : 'text-amber-300 border-amber-400/30 bg-amber-400/10'}`}>
      <Music className="w-2.5 h-2.5 shrink-0" /> {late ? 'Songs overdue' : `Songs ${hours}h`}
    </span>
  );
}

/* EVERYTHING SAID ABOUT A MISSED DEADLINE RENDERS FROM song_wait.consequence ALONE (operator ruling
 * 2026-09-16). The server decides what is TRUE (server.py _song_wait_consequence: the live mode, the
 * queue, every refusal) and these only put it into words, so no panel can promise a consequence the
 * rule will not deliver. That gap appeared three times, and it was closed here by construction, not by
 * the mode happening to be live: in shadow a client reads neither a lost-spot sentence nor a date. The
 * consequence arrives with the drawer's single-project read; until then nothing is said. */
const LATE_HEADING = 'The 24 hours to choose your songs have passed.';

function SongLateNotice({ wait, once }) {
  const c = wait?.consequence;
  // 'stopped': the team stopped the 24-hour rule on this project, so the client is told nothing about a deadline
  // (Ryan, 2026-10-12). The paragraph above already drops its timing sentences for any consequence (policyFor).
  if (!c || c === 'stopped') return null;
  return (
    <div className="rounded-lg border border-ug-status-red/30 bg-ug-status-red/10 px-3 py-2 text-[11px] text-zinc-100/85 leading-relaxed"
      data-testid="song-deadline-late" data-consequence={c}>
      {c === 'moves_date' ? (
        <><strong className="text-ug-status-red">{LATE_HEADING}</strong> Every day we wait for your songs now moves your film's delivery date back. Send them before midnight tonight, Eastern time, and your film will be ready by <strong className="text-zinc-100" data-testid="song-deadline-date">{dayWords(wait.tonight.eta)}</strong>.</>
      ) : c === 'loses_spot' ? (
        <><strong className="text-ug-status-red">The 24 hours have passed.</strong> {once
          ? 'Your film has lost its spot in the editing queue, or will at the next check. Please still send your song choices: your editor needs them to finish.'
          : 'Your project has moved back in the queue, or will at the next check. Please still answer: your editor needs the songs to finish, and answering now means no further delay.'}</>
      ) : (
        <><strong className="text-ug-status-red">{LATE_HEADING}</strong> Please send your songs as soon as you can. Your project manager will confirm your delivery date once they arrive.</>
      )}
    </div>
  );
}

function SongLateEditorLine({ project, latest }) {
  const w = project.song_wait;
  const c = w?.consequence;
  if (!c) return null;
  const text = c === 'moves_date'
    ? `No answer in 24 hours, and no project behind this one to move up. Its due date moves back with each day the songs are late: if they arrive before midnight tonight, Eastern time, it will be due ${dayWords(w.tonight.due)}.`
    : c === 'loses_spot'
      ? (latest.status === 'expired'
          ? 'No answer in 24 hours: this project moved back in your queue. The client can still answer, and you will hear the moment they do.'
          : "The client's 24 hours are up, so this project moves back in your queue at the next check. You will have an email when it does.")
      : c === 'stopped'
        ? 'No answer in 24 hours. The 24-hour song rule is stopped on this project, so nothing more in your queue moves because of it: carry on as far as you can, and your project manager will set any new due date when the songs arrive.'
        : 'No answer in 24 hours. Nothing in your queue moves on its own: carry on as far as you can, and your project manager will set any new due date when the songs arrive.';
  return <p className="text-[10px] text-amber-300 mb-2" data-testid="song-late-editor" data-consequence={c}>{text}</p>;
}

// ─── Editor / admin side ───────────────────────────────────────────────
export function SongApprovalEditor({ project, onRefresh }) {
  const proposals = project.song_proposals || [];
  const latest = proposals.length ? proposals[proposals.length - 1] : null;
  const awaitingClient = latest?.status === 'pending';
  // The project's one set has gone out: there is no second, for anyone (the server refuses too).
  const onceSent = proposals.some(isOnce);

  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState([blankSection()]);
  const [sending, setSending] = useState(false);

  const patch = (i, key, val) => setSections(s => s.map((x, j) => j === i ? { ...x, [key]: val } : x));
  const patchLink = (i, li, val) =>
    setSections(s => s.map((x, j) => j === i ? { ...x, links: x.links.map((l, k) => k === li ? val : l) } : x));

  const submit = async () => {
    const payload = sections
      .map(s => ({ heading: s.heading.trim(), links: s.links.map(l => l.trim()).filter(Boolean) }))
      .filter(s => s.links.length);
    if (!payload.length) { toast.error('Add at least one song link before sending.'); return; }
    if (payload.length > 1 && payload.some(s => !s.heading)) { toast.error(TITLES_NEEDED); return; }
    setSending(true);
    try {
      await api.post(`/projects/${project.id}/song-proposals`, { sections: payload });
      toast.success('Sent to the client. You\'ll get an email with their final songs.');
      setSections([blankSection()]);
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send song choices');
    } finally { setSending(false); }
  };

  // More than one section with songs in it: each needs its title before the set can go (the server refuses too).
  const withSongs = sections.filter(s => s.links.some(l => l.trim()));
  const titlesNeeded = withSongs.length > 1 && withSongs.some(s => !s.heading.trim());

  return (
    <div className="mt-4 border-t border-ug-border pt-4" data-testid="song-approval-editor">
      <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3 flex items-center gap-1.5">
        <Music className="w-3 h-3" strokeWidth={2} /> Song Approval
      </h3>

      {/* Plain-language explainer. This is the whole reason an editor will or won't
          use the feature, so it says what it is, who sees it, and what happens next. */}
      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border space-y-2 mb-2" data-testid="song-editor-explainer">
        <p className="text-[11px] text-zinc-100/80 leading-relaxed">
          Some clients want to okay your song picks before you start editing. If this client asked for that, send them
          one set of song choices: a section for each part of the video (Intro, Ceremony, Dance), or one section with
          several options to choose from.
        </p>
        <p className="text-[11px] text-zinc-100/80 leading-relaxed">
          They go <span className="text-ug-gold">straight to the client</span>, with no project manager in between, even on a weekend.
        </p>
        <p className="text-[11px] text-zinc-100/80 leading-relaxed">
          <span className="text-ug-gold">This is the only set they get.</span> For each section they keep any of your options and can
          add links to their own song(s). What they send is final, so use it as sent. You'll get an email with their final songs.
        </p>
      </div>

      <SongProposalHistory proposals={proposals} />

      {/* THE PENDING SET, READ BACK. While the client hasn't answered, the sender
          (and admin) need to see exactly what went out and when — before this,
          history showed only ANSWERED sets, so a pending proposal was invisible
          to the very person waiting on it (2026-08 operator ask). Read-only:
          the no-editor-prose rule is untouched, these are the already-sent links. */}
      {awaitingClient && (
        <div className="p-3 rounded-lg bg-ug-surface/50 border border-ug-border-subtle mb-2" data-testid="song-pending-readback">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-sans text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-oxidized-teal/20 text-oxidized-teal">
              Sent, awaiting reply
            </span>
            <span className="text-[9px] text-slate-600" data-testid="song-pending-sent-meta">
              Set {latest.proposal_number} · sent {formatEastern(latest.created_at, { dateOnly: true })}
              {latest.created_by_name ? ` by ${latest.created_by_name}` : ''}
            </span>
          </div>
          <div className="mt-2">
            <SentSetLinks proposal={latest} rowTestid="song-pending-link" />
          </div>
        </div>
      )}

      {awaitingClient && (() => {
        const ds = deadlineState(latest);
        if (ds && ds.expired) return null;          // past the deadline: SongLateEditorLine says what happens
        const terms = project.song_wait?.terms;
        if (terms === 'no_date' || terms === 'not_live') return (
          <p className="text-[10px] text-oxidized-teal mb-2" data-testid="song-awaiting-client">
            Waiting on the client to reply. {terms === 'no_date'
              ? 'This project has no delivery date yet, so there is no 24-hour limit on it and nothing in your queue moves.'
              : 'These went out while the song deadline rule was not switched on, so there is no 24-hour limit on them and nothing in your queue moves.'}
          </p>
        );
        // The consequence clause is stated only while the rule is live: in shadow nothing moves.
        const live = project.song_wait?.mode === 'live';
        return (
          <p className="text-[10px] text-oxidized-teal mb-2" data-testid="song-awaiting-client">
            Waiting on the client to reply{ds ? `: they have ${hoursWords(ds.hoursLeft)} left${live ? ' before the project moves back in your queue' : ''}` : ''}.
          </p>
        );
      })()}
      {['pending', 'overdue', 'expired'].includes(latest?.status) && <SongLateEditorLine project={project} latest={latest} />}

      {onceSent ? (
        <p className="text-[10px] text-slate-400 leading-relaxed" data-testid="song-once-sent-note">
          Song choices go out once per project. Anything more about songs goes to your project manager in chat.
        </p>
      ) : !open && (
        <button onClick={() => setOpen(true)} data-testid="song-approval-open"
          className="w-full py-2 rounded-lg bg-ug-gold/10 border border-ug-gold/20 text-ug-gold font-sans text-[10px] font-semibold uppercase tracking-wider hover:bg-ug-gold/20 transition-colors tap-auto">
          Send song choices to client
        </button>
      )}

      {open && !onceSent && (
        <div className="space-y-3">
          {sections.map((sec, i) => (
            <div key={i} className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
              {/* Section title. Distinguished from the link fields by TYPE and an
                  underline rather than by size — it must not add height. */}
              <div className="flex items-center gap-2">
                <input value={sec.heading} maxLength={HEADING_MAX}
                  onChange={e => patch(i, 'heading', e.target.value)}
                  placeholder={sections.length > 1 ? 'Title for this section, e.g. Intro, Ceremony, Dance' : 'Optional, e.g. Intro, Ceremony, Dance Section'}
                  data-testid={`song-heading-${i}`}
                  aria-invalid={(titlesNeeded && !sec.heading.trim() && sec.links.some(l => l.trim())) || undefined}
                  className="tap-auto flex-1 min-w-0 bg-transparent border-0 border-b border-ug-border-strong rounded-none px-0.5 pb-1 text-[12px] font-semibold text-zinc-100 outline-none transition-colors focus:border-ug-gold/50 placeholder:text-slate-600 placeholder:font-normal aria-[invalid=true]:border-ug-status-red" />
                {sections.length > 1 && (
                  <button onClick={() => setSections(s => s.filter((_, j) => j !== i))}
                    title="Remove this section"
                    className="shrink-0 text-slate-500 hover:text-ug-status-red transition-colors tap-auto">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Field height stays at the original py-1.5; only the gaps between
                  fields opened up. */}
              <div className="mt-2.5 space-y-2">
                {sec.links.map((l, li) => (
                  <div key={li} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 flex items-center gap-1.5 rounded bg-ug-surface border border-ug-border-strong px-2 py-1.5 transition-colors focus-within:border-ug-gold/40">
                      <Link2 className="w-3 h-3 text-slate-600 shrink-0" />
                      <input value={l} onChange={e => patchLink(i, li, e.target.value)}
                        placeholder="Paste song link: Musicbed, Artlist, Epidemic…"
                        data-testid={`song-link-${i}-${li}`}
                        className="tap-auto flex-1 min-w-0 bg-transparent border-0 outline-none text-[11px] text-zinc-100 placeholder:text-slate-600" />
                    </div>
                    {sec.links.length > 1 && (
                      <button onClick={() => patch(i, 'links', sec.links.filter((_, k) => k !== li))}
                        title="Remove this link"
                        className="shrink-0 text-slate-500 hover:text-ug-status-red transition-colors tap-auto">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {sec.links.length < MAX_LINKS && (
                <button onClick={() => patch(i, 'links', [...sec.links, ''])}
                  data-testid={`song-add-link-${i}`}
                  className="mt-2.5 mb-1 flex items-center gap-1 text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 tap-auto">
                  <Plus className="w-3 h-3" /> Add another song option to this section
                </button>
              )}
            </div>
          ))}

          {sections.length < MAX_SECTIONS && (
            <button onClick={() => setSections(s => [...s, blankSection()])}
              data-testid="song-add-section"
              className="text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 flex items-center gap-1 tap-auto">
              <Plus className="w-3 h-3" /> Add another section
            </button>
          )}

          <p className="text-[10px] text-amber-300 leading-relaxed" data-testid="song-once-send-warning">
            You can send song choices only once for this project. Check that every link opens before you send.
          </p>
          {titlesNeeded && (
            <p className="text-[11px] text-ug-status-red leading-relaxed" data-testid="song-titles-needed" data-tone="warning">
              {TITLES_NEEDED}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={submit} disabled={sending || titlesNeeded} data-testid="song-approval-send"
              className="flex-1 py-2 rounded-lg bg-gold-leaf text-obsidian font-sans text-[10px] font-semibold uppercase tracking-wider hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-1.5 tap-auto">
              <Send className="w-3 h-3" /> {sending ? 'Sending…' : 'Send to client'}
            </button>
            <button onClick={() => { setOpen(false); setSections([blankSection()]); }}
              className="px-3 py-2 rounded-lg border border-ug-border text-slate-400 font-sans text-[10px] font-semibold uppercase tracking-wider hover:text-zinc-100 transition-colors tap-auto">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Client side ───────────────────────────────────────────────────────
export function SongApprovalClient({ project, onRefresh }) {
  const proposals = project.song_proposals || [];
  const latest = proposals.length ? proposals[proposals.length - 1] : null;

  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Nothing sent yet — stay out of the client's way entirely.
  if (!latest) return null;
  // Keyed on the set, so its answer state belongs to that set alone.
  if (isOnce(latest)) return <SongOnceClient key={latest.proposal_number} project={project} proposal={latest} onRefresh={onRefresh} />;

  const pending = ['pending', 'overdue', 'expired'].includes(latest.status);   // overdue and expired sets can still be answered; the deadline is what passed
  const ds = deadlineState(latest);
  const toggle = (link) =>
    setSelected(s => s.includes(link) ? s.filter(x => x !== link) : [...s, link]);

  const respond = async (approved) => {
    if (!approved && !note.trim()) {
      toast.error('Let us know what you\'d like changed');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/song-proposals/${latest.proposal_number}/respond`,
        { approved, note: note.trim(), selected_links: selected });
      toast.success(approved ? 'Thanks, your editor has been told' : 'Thanks, we\'ll send new options');
      setNote(''); setSelected([]);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your answer');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-ug-border pt-4" data-testid="song-approval-client">
      <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3 flex items-center gap-1.5">
        <Music className="w-3 h-3" strokeWidth={2} /> Song Choices
      </h3>

      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border space-y-3">
        <p className="text-[11px] text-zinc-100/80 leading-relaxed">
          {pending
            ? 'We\'ve picked out music for your film. Have a listen and let us know what you think: tick the ones you want, or ask for different options.'
            : latest.status === 'approved'
              ? 'You approved these song choices.'
              : 'You asked for different options. We\'ll send a new set shortly.'}
        </p>

        {(latest.sections || []).map((sec, i) => (
          <div key={i} className="space-y-1">
            {sec.heading && (
              <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal/70">{sec.heading}</span>
            )}
            {(sec.links || []).map((link, li) => {
              const picked = pending ? selected.includes(link) : (latest.selected_links || []).includes(link);
              return (
                <div key={li} className={`flex items-center gap-2 p-1.5 rounded border transition-colors ${picked ? 'bg-ug-gold/10 border-ug-gold/30' : 'bg-ug-surface/50 border-ug-border-subtle'}`}>
                  {pending && (
                    <button onClick={() => toggle(link)} data-testid={`song-pick-${i}-${li}`}
                      title={picked ? 'Picked' : 'Use this one'}
                      className={`w-4 h-4 rounded shrink-0 border flex items-center justify-center transition-colors tap-auto ${picked ? 'bg-ug-gold border-ug-gold' : 'border-ug-border-strong hover:border-ug-gold/50'}`}>
                      {picked && <Check className="w-2.5 h-2.5 text-obsidian" strokeWidth={3} />}
                    </button>
                  )}
                  {!pending && picked && <Check className="w-3 h-3 text-ug-gold shrink-0" strokeWidth={3} />}
                  <a href={link} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 flex-1 truncate">
                    {hostOf(link)}
                  </a>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-600 shrink-0" />
                </div>
              );
            })}
          </div>
        ))}

        {pending && ds && !ds.expired && (
          <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] text-zinc-100/85 leading-relaxed" data-testid="song-deadline-warning">
            <strong className="text-amber-300">Please answer within 24 hours: {hoursWords(ds.hoursLeft)} left.</strong> Your editor cannot go further without your okay,
            and every other client in their queue has a promised date.
            {/* The consequence is stated only while the rule is live (song_wait.mode): in shadow nothing moves. */}
            {project.song_wait?.mode === 'live' && ' If we have not heard from you by then, your project moves back in the queue and the next one takes its place. There is no flexibility on this; it is what keeps everyone\'s dates honest, including yours.'}
          </div>
        )}
        {pending && <SongLateNotice wait={project.song_wait} once={false} />}
        {pending ? (
          <div className="space-y-2 pt-1">
            <NoteBox value={note} onChange={setNote}
              placeholder="Add a note (optional), e.g. “Option B please, but use the other if it works better”" />
            <div className="flex gap-2">
              <button onClick={() => respond(true)} disabled={busy} data-testid="song-approve"
                className="flex-1 py-2 rounded-lg bg-gold-leaf text-obsidian font-sans text-[10px] font-semibold uppercase tracking-wider hover:opacity-90 disabled:opacity-50 transition-opacity tap-auto">
                Good to go
              </button>
              <button onClick={() => respond(false)} disabled={busy} data-testid="song-request-changes"
                className="flex-1 py-2 rounded-lg border border-ug-border text-slate-300 font-sans text-[10px] font-semibold uppercase tracking-wider hover:text-zinc-100 hover:border-ug-border-strong disabled:opacity-50 transition-colors tap-auto">
                Request changes
              </button>
            </div>
          </div>
        ) : latest.client_note ? (
          <p className="text-[12px] text-zinc-100/85 leading-relaxed pt-1 whitespace-pre-wrap break-words" data-testid="song-client-note-sent">{latest.client_note}</p>
        ) : null}
      </div>
    </div>
  );
}

/* THE CLIENT'S ONE ANSWER to the project's one set. Per section they keep any of our options
 * and/or add links to their own song(s); every section needs a song before they can send.
 * The server applies the same rules (routes/song_approval.py _clean_answers) and has the
 * last word: this only keeps the button honest. */
function SongOnceClient({ project, proposal, onRefresh }) {
  const sections = proposal.sections || [];
  const [answers, setAnswers] = useState(() => sections.map(() => ({ picks: [], own_links: [] })));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const pending = ['pending', 'overdue', 'expired'].includes(proposal.status);   // overdue and expired can still be answered; the deadline is what passed
  // Only an "approved" once set was answered. A set can also close WITHOUT an answer: the
  // deadline sweep auto-closes it when the first draft is already cut. Telling that client
  // "your song choices are in" would be false.
  const answered = proposal.status === 'approved';
  const ds = deadlineState(proposal);
  const multi = sections.length > 1;

  const update = (i, fn) => setAnswers(a => a.map((x, j) => (j === i ? fn(x) : x)));
  const togglePick = (i, link) => update(i, x => ({ ...x, picks: x.picks.includes(link) ? x.picks.filter(l => l !== link) : [...x.picks, link] }));
  const setOwn = (i, li, val) => update(i, x => ({ ...x, own_links: x.own_links.map((l, k) => (k === li ? val : l)) }));
  const addOwn = (i) => update(i, x => ({ ...x, own_links: [...x.own_links, ''] }));
  const removeOwn = (i, li) => update(i, x => ({ ...x, own_links: x.own_links.filter((_, k) => k !== li) }));
  const hasSong = (x) => x.picks.length > 0 || x.own_links.some(l => l.trim());
  const ready = answers.every(hasSong);

  const submit = async () => {
    if (!ready) { toast.error('Every part needs at least one song before you can send.'); return; }
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/song-proposals/${proposal.proposal_number}/respond`, {
        answers: answers.map(x => ({ picks: x.picks, own_links: x.own_links.map(l => l.trim()).filter(Boolean) })),
        note: note.trim(),
      });
      toast.success('Thanks, your song choices are in');
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your song choices');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-ug-border pt-4" data-testid="song-approval-client">
      <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3 flex items-center gap-1.5">
        <Music className="w-3 h-3" strokeWidth={2} /> Song Choices
      </h3>

      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border space-y-3">
        {pending ? (
          <p className="text-[11px] text-zinc-100/85 leading-relaxed" data-testid="song-once-policy">
            {policyFor(project.song_wait)}
          </p>
        ) : answered ? (
          <p className="text-[11px] text-zinc-100/85 leading-relaxed" data-testid="song-once-answered">
            Your song choices are in. Any song questions from here go through your project chat.
          </p>
        ) : (
          <p className="text-[11px] text-zinc-100/85 leading-relaxed" data-testid="song-once-closed">
            These song choices closed without an answer because your first draft was already underway. Any song questions go through your project chat.
          </p>
        )}

        {pending && ds && !ds.expired && (
          <p className="text-[11px] font-semibold text-amber-300" data-testid="song-deadline-warning">
            {hoursWords(ds.hoursLeft)} left to reply.
          </p>
        )}
        {pending && <SongLateNotice wait={project.song_wait} once />}

        {pending ? sections.map((sec, i) => {
          const a = answers[i];
          return (
            <div key={i} className="p-2.5 rounded-lg bg-ug-surface/50 border border-ug-border-subtle space-y-1.5" data-testid={`song-once-section-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal/80 truncate">
                  {sec.heading || (multi ? `Part ${i + 1}` : 'Our options')}
                </span>
                <span data-testid={`song-section-state-${i}`}
                  className={`tap-auto shrink-0 font-sans text-[9px] font-semibold ${hasSong(a) ? 'text-oxidized-teal' : 'text-slate-500'}`}>
                  {hasSong(a) ? 'Song chosen' : 'Choose a song or add a link'}
                </span>
              </div>

              {(sec.links || []).map((link, li) => {
                const picked = a.picks.includes(link);
                return (
                  <div key={li} className={`flex items-center gap-2 p-1.5 rounded border transition-colors ${picked ? 'bg-ug-gold/10 border-ug-gold/30' : 'bg-ug-surface/50 border-ug-border-subtle'}`}>
                    <button type="button" onClick={() => togglePick(i, link)} data-testid={`song-pick-${i}-${li}`}
                      role="checkbox" aria-checked={picked} title={picked ? 'Keeping this one' : 'Keep this one'}
                      className={`w-4 h-4 rounded shrink-0 border flex items-center justify-center transition-colors tap-auto ${picked ? 'bg-ug-gold border-ug-gold' : 'border-ug-border-strong hover:border-ug-gold/50'}`}>
                      {picked && <Check className="w-2.5 h-2.5 text-obsidian" strokeWidth={3} />}
                    </button>
                    <a href={link} target="_blank" rel="noopener noreferrer" title={link}
                      className="tap-auto text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 flex-1 truncate">
                      {trackLabel(link) || hostOf(link)}
                    </a>
                    <ExternalLink className="w-2.5 h-2.5 text-slate-600 shrink-0" />
                  </div>
                );
              })}

              {a.own_links.map((l, li) => (
                <div key={`own-${li}`} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 flex items-center gap-1.5 rounded bg-ug-surface border border-ug-border-strong px-2 py-1.5 transition-colors focus-within:border-ug-gold/40">
                    <Link2 className="w-3 h-3 text-ug-gold/70 shrink-0" />
                    <input value={l} onChange={e => setOwn(i, li, e.target.value)}
                      placeholder="Paste a link to your own song"
                      data-testid={`song-own-link-${i}-${li}`}
                      className="tap-auto flex-1 min-w-0 bg-transparent border-0 outline-none text-[11px] text-zinc-100 placeholder:text-slate-600" />
                  </div>
                  <button type="button" onClick={() => removeOwn(i, li)} title="Remove this link" aria-label="Remove this link"
                    className="shrink-0 text-slate-500 hover:text-ug-status-red transition-colors tap-auto">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {a.own_links.length < MAX_LINKS && (
                <button type="button" onClick={() => addOwn(i)} data-testid={`song-add-own-${i}`}
                  className="flex items-center gap-1 text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 tap-auto">
                  <Plus className="w-3 h-3" /> Add a link to your own song
                </button>
              )}
            </div>
          );
        }) : answered ? <FinalSongs proposal={proposal} /> : null}

        {pending ? (
          <div className="space-y-2 pt-1">
            <NoteBox value={note} onChange={setNote}
              placeholder="Add a note (optional), e.g. “use the instrumental version”. Please don't use this to ask for more options." />
            {!ready && (
              <p className="text-[10px] text-slate-400" data-testid="song-once-not-ready">Every part needs at least one song before you can send.</p>
            )}
            <button type="button" onClick={submit} disabled={busy || !ready} data-testid="song-once-submit"
              className="w-full py-2 rounded-lg bg-gold-leaf text-obsidian font-sans text-[10px] font-semibold uppercase tracking-wider hover:opacity-90 disabled:opacity-50 transition-opacity tap-auto">
              {busy ? 'Sending…' : 'Send my song choices'}
            </button>
          </div>
        ) : proposal.client_note ? (
          <p className="text-[12px] text-zinc-100/85 leading-relaxed pt-1 whitespace-pre-wrap break-words" data-testid="song-client-note-sent">{proposal.client_note}</p>
        ) : null}
      </div>
    </div>
  );
}

/* THE FINAL SONGS of an answered once set, per section: our options the client kept, then
 * the client's own links, each labelled. Shared by the editor's history and the client's
 * read-back, so both sides see the same list. */
function FinalSongs({ proposal }) {
  const answers = proposal.answers || [];
  const multi = answers.length > 1;
  return (
    <div className="flex flex-col gap-2" data-testid={`song-final-${proposal.proposal_number}`}>
      {answers.map((a, si) => (
        <div key={si} className="rounded-md bg-ug-surface/60 border border-ug-border-subtle divide-y divide-ug-border-subtle overflow-hidden">
          {(a.heading || multi) && (
            <div className="px-2.5 py-1 font-sans text-[10px] font-semibold text-zinc-100/80">{a.heading || `Part ${si + 1}`}</div>
          )}
          {[...(a.picks || []).map(l => [l, 'pick']), ...(a.own_links || []).map(l => [l, 'own'])].map(([l, kind], li) => (
            <a key={`${kind}-${li}`} href={l} target="_blank" rel="noopener noreferrer" title={l}
               data-testid={`song-final-${proposal.proposal_number}-${si}-${kind}`}
               className="tap-auto group flex items-center gap-2 px-2.5 py-1.5 hover:bg-ug-surface transition-colors">
              {kind === 'own'
                ? <Link2 className="w-3 h-3 shrink-0 text-ug-gold" aria-label="Client's own song" />
                : <Check className="w-3 h-3 shrink-0 text-ug-gold" strokeWidth={3} aria-label="Kept from our options" />}
              <span className="font-sans text-[11px] text-oxidized-teal group-hover:text-oxidized-teal/80 truncate min-w-0 flex-1">
                {trackLabel(l) || hostOf(l)}
              </span>
              <span className={`font-sans text-[9px] font-semibold uppercase tracking-wider shrink-0 ${kind === 'own' ? 'text-ug-gold' : 'text-slate-500'}`}>
                {kind === 'own' ? 'Their own' : 'Our option'}
              </span>
              <ExternalLink className="w-2.5 h-2.5 shrink-0 text-slate-700 group-hover:text-slate-500 transition-colors" />
            </a>
          ))}
        </div>
      ))}
    </div>
  );
}

/* EXACTLY WHAT WENT OUT, for any set. Every link the editor sent, in section order,
 * each an anchor carrying the full URL (href + title) with the host beside the track
 * name, so a link the client calls broken can be checked from the panel. Picks are
 * marked with a gold check instead of the note icon. Read-only: these are links that
 * were already sent, so the no-editor-prose rule is untouched. */
function SentSetLinks({ proposal, rowTestid }) {
  const picked = new Set(proposal.selected_links || []);
  return (
    <div className="rounded-md bg-ug-surface/60 border border-ug-border-subtle divide-y divide-ug-border-subtle overflow-hidden">
      {(proposal.sections || []).flatMap((sec, si) =>
        (sec.links || []).map((l, li) => {
          const track = trackLabel(l);
          const isPicked = picked.has(l);
          return (
            <a key={`${si}-${li}`} href={l} target="_blank" rel="noopener noreferrer" title={l}
               data-testid={`${rowTestid}-${si}-${li}`} data-picked={isPicked ? 'true' : 'false'}
               className="tap-auto group flex items-center gap-2 px-2.5 py-1.5 hover:bg-ug-surface transition-colors">
              {isPicked
                ? <Check className="w-3 h-3 shrink-0 text-ug-gold" strokeWidth={3} aria-label="Client picked" />
                : <Music className="w-3 h-3 shrink-0 text-slate-600 group-hover:text-oxidized-teal transition-colors" />}
              {(sec.heading || '').trim() && (
                <span className="font-sans text-[10px] text-slate-500 shrink-0 max-w-[38%] truncate">
                  {sec.heading.trim()}
                </span>
              )}
              <span className="font-sans text-[11px] text-oxidized-teal group-hover:text-oxidized-teal/80 truncate min-w-0 flex-1">
                {track || hostOf(l)}
              </span>
              {track && (
                <span className="font-mono text-[9px] text-slate-600 shrink-0 max-w-[30%] truncate">{hostOf(l)}</span>
              )}
              <ExternalLink className="w-2.5 h-2.5 shrink-0 text-slate-700 group-hover:text-slate-500 transition-colors" />
            </a>
          );
        }))}
    </div>
  );
}

/* EVERY STATE A SET CAN REACH (routes/song_approval.py, app/song_deadline.py). The badge
 * used to read "Changes asked" for anything not approved, so a set nobody answered
 * (expired at 24 hours, or auto-closed when the project reached First Draft Complete)
 * claimed the client had asked for changes. An unknown status shows its own name.
 * An answered once set is stored as "approved" but reads "Final songs": the client did not
 * approve ours, they chose, possibly with songs of their own. */
const SET_STATUS = {
  approved: { label: 'Approved', cls: 'bg-oxidized-teal/20 text-oxidized-teal' },
  changes_requested: { label: 'Changes asked', cls: 'bg-ug-gold/20 text-ug-gold' },
  overdue: { label: 'No answer in 24h', cls: 'bg-amber-400/15 text-amber-300' },
  expired: { label: 'No answer in 24h', cls: 'bg-amber-400/15 text-amber-300' },
  auto_closed: { label: 'Closed unanswered', cls: 'bg-white/5 text-slate-400' },
  superseded: { label: 'Replaced unanswered', cls: 'bg-white/5 text-slate-400' },
};
const setStatus = (p) => (isOnce(p) && p.status === 'approved')
  ? { label: 'Final songs', cls: 'bg-oxidized-teal/20 text-oxidized-teal' }
  : SET_STATUS[p.status] || { label: String(p.status || 'unknown'), cls: 'bg-white/5 text-slate-400' };

// ─── Shared: earlier rounds ────────────────────────────────────────────
function SongProposalHistory({ proposals }) {
  const answered = (proposals || []).filter(p => p.status !== 'pending');
  if (!answered.length) return null;
  return (
    /* Each answered set is its own card, so they need real air between them — at
       space-y-1.5 two bordered cards sat 6px apart and read as one block with a
       stray line through it. The inner blocks carry their own padding now, so the
       card needs enough of its own not to crowd them.
       FLEX + GAP, NOT space-y-*. space-y compiles to `> * + *`, which matches DOM
       CHILDREN — and in the dev preview the visual-edits babel plugin wraps every
       mapped list in a <span style="display:contents">, so the cards become
       grandchildren and the utility silently does nothing. It is dev-only and the
       production bundle is clean, but that means spacing verified in the preview
       does not predict production, in either direction. `gap` is a layout property
       and `display:contents` promotes the children into this flex box, so it
       applies in both. See KNOWN-ISSUES #26. */
    <div className="flex flex-col gap-3 mb-2" data-testid="song-approval-history">
      {answered.map(p => (
        <div key={p.proposal_number} className="p-3 rounded-lg bg-ug-surface/50 border border-ug-border-subtle">
          <div className="flex items-center gap-2">
            <span className={`font-sans text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${setStatus(p).cls}`}
                  data-testid={`song-set-status-${p.proposal_number}`}>
              {setStatus(p).label}
            </span>
            <span className="text-[9px] text-slate-600">
              Set {p.proposal_number}
              {p.created_at ? ` · sent ${formatEastern(p.created_at, { dateOnly: true })}` : ''}
            </span>
          </div>
          {isOnce(p) && p.answers?.length > 0 ? (
            <div className="mt-2">
              <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                Final songs
              </span>
              <FinalSongs proposal={p} />
            </div>
          ) : p.selected_links?.length > 0 && (
            /* EVERY <a> IS min-height:44px GLOBALLY (index.css), not just the ones
               carrying data-testid — so a bare list of links renders as a column of
               44px blocks with the text floating in the middle of each. `tap-auto` is
               the documented opt-out and these rows need it or the section reads as
               three paragraphs with nothing in them. */
            <div className="mt-2" data-testid={`song-picked-${p.proposal_number}`}>
              <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                Client picked
              </span>
              <div className="rounded-md bg-ug-surface/60 border border-ug-border-subtle divide-y divide-ug-border-subtle overflow-hidden">
                {p.selected_links.map((l, i) => {
                  const sec = sectionFor(p, l);
                  const track = trackLabel(l);
                  return (
                    <a key={`${l}-${i}`} href={l} target="_blank" rel="noopener noreferrer"
                       title={l}
                       data-testid={`song-picked-${p.proposal_number}-${i}`}
                       className="tap-auto group flex items-center gap-2 px-2.5 py-1.5 hover:bg-ug-surface transition-colors">
                      <Music className="w-3 h-3 shrink-0 text-slate-600 group-hover:text-oxidized-teal transition-colors" />
                      {/* Section is context, the track is the answer — so the section
                          is muted and fixed-width-ish, and the track takes the colour
                          and the remaining room. Both on one line, tight. */}
                      {sec && (
                        <span className="font-sans text-[10px] text-slate-500 shrink-0 max-w-[38%] truncate">
                          {sec}
                        </span>
                      )}
                      <span className="font-sans text-[11px] text-oxidized-teal group-hover:text-oxidized-teal/80 truncate min-w-0 flex-1">
                        {track || hostOf(l)}
                      </span>
                      <ExternalLink className="w-2.5 h-2.5 shrink-0 text-slate-700 group-hover:text-slate-500 transition-colors" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
          {p.client_note && (
            /* THE NOTE IS THE INSTRUCTION, and it outranks the list above it. The
               picks say which track; this says what to do with it — "Golden by Utah
               but with NO oohs and Ahhs" is the difference between the right edit and
               a redo. It used to render as 10px muted italic under the list, which is
               the treatment this app uses for asides, so the most actionable thing in
               the panel read as a footnote and was easy to skim past.
               Labelled, boxed, and given a teal edge so the eye lands on it. */
            <div className="mt-2 rounded-md bg-ug-surface/60 border border-ug-border-subtle border-l-2 border-l-oxidized-teal px-3 py-2"
                 data-testid={`song-note-${p.proposal_number}`}>
              <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal block mb-1">
                What they said
              </span>
              {/* Not italic and not quote-wrapped: the label already says whose words
                  these are, and italic at this size costs legibility for nothing. */}
              <p className="font-sans text-[12px] text-zinc-100/90 leading-relaxed whitespace-pre-wrap">
                {p.client_note}
              </p>
            </div>
          )}
          {(p.sections || []).length > 0 && (
            <div className="mt-2" data-testid={`song-sent-${p.proposal_number}`}>
              <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                What was sent
              </span>
              <SentSetLinks proposal={p} rowTestid={`song-sent-${p.proposal_number}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default SongApprovalEditor;
